import { useMemo } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Alert, Chip } from "@andorhealth/clientuireact.designsystem";
import { CHANNEL_CODES } from "@/lib/catalog-api";
import { StaffAppHeader } from "@/components/StaffAppHeader";
import {
  ConciergeChecking,
  ConciergeUnavailable,
} from "@/components/ConciergeUnavailable";
import { ConfigurationProvider } from "./ConfigurationProvider";
import { useConfiguration } from "./hooks/useConfiguration";
import { useUnsavedChanges } from "./hooks/useUnsavedChanges";
import { ToastProvider } from "./components/ToastProvider";
import { UnsavedChangesProvider } from "./components/UnsavedChangesProvider";
import { StatusCard } from "./components/StatusCard";
import { Surface } from "./components/Surface";
import { WizardLayout } from "./components/WizardLayout";

function Hub() {
  const {
    catalog,
    channels,
    requestTypes,
    wakePhrase,
    voiceAgentId,
    voiceBindings,
    openWizard,
    openPolicy,
    visiblePolicies,
  } = useConfiguration();

  const hubStats = useMemo(() => {
    const enabledChannels = channels.filter((channel) => channel.isEnabled).length;
    const missingChannels = CHANNEL_CODES.filter(
      (code) =>
        !channels.some(
          (channel) =>
            channel.channelCode.toUpperCase() === code && channel.isEnabled,
        ),
    );
    const typesMissingPolicy = requestTypes.filter(
      (item) => item.isEnabled && !item.escalationPolicyId,
    ).length;
    return {
      enabledChannels,
      missingChannels,
      policyCount: visiblePolicies.length,
      typesMissingPolicy,
      hasDefault: Boolean(catalog?.configuration?.defaultEscalationPolicyId),
    };
  }, [
    catalog?.configuration?.defaultEscalationPolicyId,
    channels,
    requestTypes,
    visiblePolicies.length,
  ]);

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        <StatusCard
          title="Notifications"
          value={`${hubStats.enabledChannels} of ${CHANNEL_CODES.length}`}
          ok={hubStats.missingChannels.length === 0}
          detail={
            hubStats.missingChannels.length
              ? "Some contact methods are still off"
              : "Contact methods are ready"
          }
          actionLabel="Edit"
          onClick={() => openWizard(0)}
        />
        <StatusCard
          title="Coverage"
          value={`${hubStats.policyCount}`}
          ok={hubStats.policyCount > 0}
          detail="Who is called, and in what order"
          actionLabel="Edit"
          onClick={() => openWizard(1)}
        />
        <StatusCard
          title="Requests"
          value={`${requestTypes.length}`}
          ok={hubStats.typesMissingPolicy === 0}
          detail={
            hubStats.typesMissingPolicy
              ? `${hubStats.typesMissingPolicy} need a coverage path`
              : "Each request has a coverage path"
          }
          actionLabel="Edit"
          onClick={() => openWizard(4)}
        />
        <StatusCard
          title="Wake phrase"
          value={wakePhrase || "Not set"}
          ok={Boolean(wakePhrase) && hubStats.hasDefault}
          detail={
            hubStats.hasDefault
              ? "Everyday settings look good"
              : "Finish everyday settings"
          }
          actionLabel="Edit"
          onClick={() => openWizard(5)}
        />
        <StatusCard
          title="Voice assistant"
          value={voiceAgentId || "Not set"}
          ok={Boolean(voiceAgentId)}
          detail={
            voiceBindings.length
              ? `${voiceBindings.length} org/site/location override(s)`
              : "Tenant default; add overrides by place"
          }
          actionLabel="Edit"
          onClick={() => openWizard(6)}
        />
      </Box>
      <Surface>
        <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Coverage paths</Typography>
        <Stack spacing={1.25}>
          {visiblePolicies.map((policy) => (
            <Box
              key={policy.escalationPolicyId}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                alignItems: "center",
                border: "1px solid #e4e7ec",
                p: 1.5,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700 }}>
                  {policy.displayName}
                  {policy.code.toUpperCase() === "EMERGENCY" ? (
                    <Chip
                      size="small"
                      label="Emergency"
                      sx={{
                        ml: 1,
                        borderRadius: "2px",
                        height: 22,
                        fontWeight: 700,
                      }}
                    />
                  ) : null}
                </Typography>
                <Typography sx={{ color: "text.secondary", fontSize: "0.9rem" }}>
                  {policy.tiers
                    .map((tier) => tier.displayName || tier.roleCode)
                    .join(" → ") || "No steps yet"}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => openPolicy(policy)}
              >
                Edit
              </Button>
            </Box>
          ))}
          {!visiblePolicies.length ? (
            <Typography sx={{ color: "text.secondary" }}>
              No coverage paths yet.
            </Typography>
          ) : null}
        </Stack>
      </Surface>
    </>
  );
}

function ScreenHeader() {
  const { mode, loading, reload, openWizard } = useConfiguration();
  const { requestHub } = useUnsavedChanges();

  return (
    <StaffAppHeader
      title="Patient Concierge Settings"
      active="settings"
      actions={
        mode === "hub" ? (
          <>
            <Button
              variant="outlined"
              size="small"
              onClick={() => void reload()}
              disabled={loading}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => openWizard(0)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Edit
            </Button>
          </>
        ) : (
          <Button
            variant="outlined"
            size="small"
            onClick={requestHub}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Done
          </Button>
        )
      }
    />
  );
}

function ScreenBody() {
  const { mode, loading, loadError, unavailable, catalog, reload } =
    useConfiguration();

  if (unavailable) return <ConciergeUnavailable />;
  if (loading && !catalog) return <ConciergeChecking />;
  if (!catalog) {
    return (
      <Box sx={{ minHeight: "100dvh", bgcolor: "background.default", py: 4 }}>
        <Container maxWidth="lg">
          <Alert
            type="error"
            title={loadError || "Unable to load catalog"}
            actionLabel="Refresh"
            onAction={() => void reload()}
          />
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: "background.default",
        py: { xs: 3, md: 4 },
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
        <Stack spacing={3}>
          <ScreenHeader />
          {loadError ? <Alert type="error" title={loadError} /> : null}
          {mode === "hub" ? <Hub /> : <WizardLayout />}
        </Stack>
      </Container>
    </Box>
  );
}

export function ConfigurationScreen() {
  return (
    <ToastProvider>
      <ConfigurationProvider>
        <UnsavedChangesProvider>
          <ScreenBody />
        </UnsavedChangesProvider>
      </ConfigurationProvider>
    </ToastProvider>
  );
}
